import { Navigate, Link } from "react-router-dom";
import { ArrowRight, Clock, CheckCircle2, MessageCircle } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { GUIDE_PAGES, GUIDES_HUB } from "@/data/guidePages";
import { articleSchema, breadcrumbSchema, faqSchema, canonical } from "@/lib/seo";
import { AR_GUIDE_SLUGS } from "@/data/slugs";
import { PRICE_GUIDE, PRICING_NOTE, REPORT_PROMISE, waInspectorHref } from "@/lib/business";

const GUIDE_LABELS = {
  "what-is-snagging-dubai": "What is snagging?",
  "snagging-vs-handover-vs-dlp": "Snagging vs handover vs DLP",
  "snagging-cost-dubai-2026": "Snagging costs in 2026",
  "room-by-room-snagging-checklist": "Room-by-room checklist",
  "when-to-book-snagging-inspection": "When to book an inspection",
  "send-snagging-report-to-developer": "Sending your report to the developer",
  "villa-vs-apartment-snagging": "Villa vs apartment snagging",
  "dlp-warranty-11-month-inspection": "The 11-month DLP inspection",
  "waterproofing-moisture-problems-uae": "Waterproofing & moisture problems",
  "property-snagging-report-dubai": "Inside a snagging report",
};

const publishedLabel = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

export default function GuidePage({ slug }) {
  const data = GUIDE_PAGES[slug];
  if (!data) return <Navigate to="/404" replace />;

  const path = `${GUIDES_HUB}/${slug}`;
  const breadcrumbs = [
    { name: "Guides", path: GUIDES_HUB },
    { name: data.h1, path },
  ];
  const jsonLd = [
    articleSchema({ title: data.h1, description: data.metaDescription, path, datePublished: data.datePublished }),
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Guides", path: GUIDES_HUB }, { name: data.h1, path }]),
    faqSchema(data.faqs),
  ];
  // hreflang pairing for guides that have an Arabic version.
  const alternates = AR_GUIDE_SLUGS.includes(slug)
    ? [
        { hreflang: "en-AE", href: canonical(path) },
        { hreflang: "ar-AE", href: canonical(`/ar/guides/${slug}`) },
        { hreflang: "x-default", href: canonical(path) },
      ]
    : null;

  return (
    <PageShell breadcrumbs={breadcrumbs}>
      {({ onQuote }) => (
        <>
          <Seo title={data.title} description={data.metaDescription} path={path} type="article" jsonLd={jsonLd} alternates={alternates} />

          {/* Hero */}
          <section className="wrap pt-6 pb-10 lg:pt-10 lg:pb-12">
            <span className="eyebrow">Fedari guide</span>
            <h1 className="text-[clamp(1.9rem,4vw,3rem)] leading-[1.07] tracking-[-0.03em] mt-3 mb-5 max-w-[24ch]">{data.h1}</h1>
            <p className="text-[1.1rem] text-bodytext max-w-[64ch] mb-4">{data.excerpt}</p>
            <div className="flex flex-wrap items-center gap-2.5 mb-7 text-xs text-muted">
              <span className="inline-flex items-center gap-1.5 bg-soft border border-line rounded-full px-3 py-1.5">
                <Clock size={13} className="text-blue" aria-hidden="true" /> {data.readingMinutes} min read
              </span>
              <span className="inline-flex items-center bg-soft border border-line rounded-full px-3 py-1.5">
                Published {publishedLabel(data.datePublished)}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button onClick={onQuote} data-testid="guide-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                Get quote &amp; availability <ArrowRight size={17} />
              </button>
              <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" data-testid="guide-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
                <MessageCircle size={17} /> WhatsApp Fedari
              </a>
            </div>
          </section>

          {/* Body */}
          <article className="wrap pb-12 max-w-[80ch]" data-testid="guide-body">
            {data.sections.map((sec, i) => (
              <section key={i} className="mb-9">
                <h2 className="text-xl mb-3">{sec.h}</h2>
                {(sec.paras || []).map((p, j) => (
                  <p key={j} className="text-bodytext leading-relaxed mb-3">{p}</p>
                ))}
                {sec.list && (
                  <ul className="space-y-2.5 mt-2">
                    {sec.list.map((it, k) => (
                      <li key={k} className="flex gap-2.5 text-bodytext">
                        <CheckCircle2 size={18} className="text-blue flex-none mt-0.5" aria-hidden="true" /> {it}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {data.showPriceGuide && (
              <section className="mb-9" data-testid="guide-price-guide">
                <h2 className="text-xl mb-4">2026 guide prices by property type</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PRICE_GUIDE.map((row) => (
                    <div key={row.type} className="border border-line rounded-xl p-4 bg-white flex items-center justify-between gap-3">
                      <span className="text-sm text-bodytext">{row.type}</span>
                      <span className="font-display text-blue text-sm">{row.price}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted mt-4">{PRICING_NOTE}</p>
              </section>
            )}
          </article>

          {/* CTA band */}
          <section className="wrap pb-14">
            <div className="bg-soft border border-line rounded-2xl p-6 lg:p-8 max-w-[80ch]">
              <h2 className="text-xl mb-2">Ready for a professional inspection?</h2>
              <p className="text-bodytext mb-5">Independent, licensed inspection with a photo-led report {REPORT_PROMISE}.</p>
              <button onClick={onQuote} className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                Get quote &amp; availability <ArrowRight size={16} />
              </button>
            </div>
          </section>

          {/* FAQ */}
          <section className="wrap pb-14">
            <h2 className="text-2xl mb-5">FAQs</h2>
            <div className="space-y-4 max-w-[80ch]">
              {data.faqs.map((f, i) => (
                <div key={i} className="border border-line rounded-xl p-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-bodytext text-[0.95rem]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related */}
          <section className="wrap pb-16">
            <h2 className="text-xl mb-4">Related guides &amp; services</h2>
            <div className="flex flex-wrap gap-3">
              {data.relatedGuides.map((g) => (
                <Link key={g} to={`${GUIDES_HUB}/${g}`} data-testid={`guide-related-${g}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink font-medium hover:border-blue hover:text-blue transition-colors">
                  {GUIDE_LABELS[g] || g} <ArrowRight size={15} />
                </Link>
              ))}
              {data.relatedServices.map((s) => (
                <Link key={s.href} to={s.href}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-line text-ink font-medium hover:border-blue hover:text-blue transition-colors">
                  {s.label} <ArrowRight size={15} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
